import {
  useRef,
  useId,
  useState,
  useCallback,
  useEffect,
  KeyboardEventHandler,
} from 'react';
import { Text, Icon } from 'components';
import { toast } from 'react-toastify';
import { ERROR_STRINGS } from '../../constants';

type Props = {
  title: string;
  label: string;
  sampleSrc: string;
  capture?: 'user' | 'environment';
  name?: string;
  disabled?: boolean;
  onCapture?: (file: File) => void;
};

const AR = 16 / 10;

const CameraCaptureCard = ({
  title,
  label,
  sampleSrc,
  capture = 'environment',
  name,
  disabled,
  onCapture,
}: Props) => {
  // State
  const [isShooting, setIsShooting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Ref
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const inputId = useId();

  useEffect(() => {
    if (!isShooting || !stream || !videoRef.current) return;
    const v = videoRef.current;
    v.srcObject = stream;
    v.play();

    return () => {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [isShooting, stream]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFile = useCallback(
    (file: File) => {
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });
      onCapture?.(file);
    },
    [onCapture],
  );

  const openCamera = useCallback(async () => {
    if (disabled || isShooting) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(ERROR_STRINGS.CAMERA_NOT_SUPPORTED);
      return;
    }

    try {
      const supported =
        navigator.mediaDevices.getSupportedConstraints?.() ?? {};
      const videoConstraints: MediaTrackConstraints = {
        facingMode: capture,
        width: { ideal: 1280 },
        height: { ideal: Math.round(1280 / AR) },
      };
      if (supported.aspectRatio) {
        videoConstraints.aspectRatio = { ideal: AR };
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });
      setStream(mediaStream);
      setIsShooting(true);
    } catch (e) {
      console.error(e);
      toast.error(ERROR_STRINGS.CAMERA_ACCESS_DENIED);
    }
  }, [capture, disabled, isShooting]);

  function drawCoverToCanvas(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    targetW: number,
    targetH: number,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const videoAR = vw / vh;
    const targetAR = targetW / targetH;

    let sx = 0,
      sy = 0,
      sw = vw,
      sh = vh;

    if (videoAR > targetAR) {
      sh = vh;
      sw = Math.round(vh * targetAR);
      sx = Math.round((vw - sw) / 2);
    } else {
      sw = vw;
      sh = Math.round(vw / targetAR);
      sy = Math.round((vh - sh) / 2);
    }

    canvas.width = targetW;
    canvas.height = targetH;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);
  }

  function pickTargetSize(video: HTMLVideoElement) {
    const srcW = video.videoWidth || 1280;
    const viewportW = Math.ceil(
      (window.innerWidth || 360) * (window.devicePixelRatio || 1),
    );
    const MAX_W = 1280;
    const MIN_W = 640;

    const w = Math.max(MIN_W, Math.min(srcW, viewportW, MAX_W));
    const h = Math.round(w / AR);
    return { w, h };
  }

  const shoot = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const { w, h } = pickTargetSize(video);
    drawCoverToCanvas(video, canvas, w, h);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `${name ?? 'photo'}_${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        handleFile(file);
        setIsShooting(false);
      },
      'image/jpeg',
      0.9,
    );
  }, [handleFile, name]);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        isShooting ? shoot() : openCamera();
      }
    },
    [isShooting, openCamera, shoot],
  );

  return (
    <div className="px-2 flex flex-col gap-2">
      <Text
        fontWeight="font-medium"
        className="text-xs 2xs:text-sm xs:text-base"
      >
        {title}
      </Text>

      {!isShooting && !previewUrl && (
        <div
          role="button"
          tabIndex={0}
          aria-labelledby={inputId}
          onClick={openCamera}
          onKeyDown={handleKeyDown}
          className={`rounded-xl border-dashed border-2 p-6 ${
            disabled
              ? 'border-neutral-300 opacity-60'
              : 'border-orange-500 cursor-pointer'
          }`}
        >
          <img
            className="w-10/12 rounded-xl mx-auto pointer-events-none select-none"
            src={sampleSrc}
            alt=""
          />
          <div className="flex justify-center items-center gap-2 mt-4 pointer-events-none select-none">
            <Icon icon="camera" />
            <Text
              fontWeight="font-medium"
              textColor="text-neutral-550"
              className="text-xs 2xs:text-sm xs:text-base"
            >
              <span id={inputId}>{label}</span>
            </Text>
          </div>
        </div>
      )}

      {!isShooting && previewUrl && (
        <div className="mt-4 relative aspect-[16/10]">
          <img
            src={previewUrl}
            alt="captured"
            className="w-full h-full object-cover rounded-xl"
          />
          <button
            className="absolute bottom-2 right-2 bg-white/80 p-2 rounded-full"
            onClick={openCamera}
          >
            <Icon icon="camera" />
          </button>
        </div>
      )}

      {isShooting && (
        <div
          className="relative mt-4 rounded-xl overflow-hidden border-2 border-orange-500 aspect-[16/10]"
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <video
            ref={videoRef}
            playsInline
            className="w-full h-full object-cover"
            muted
            autoFocus
          />
          <div
            onClick={shoot}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/80 rounded-full bg-white w-14 h-14 p-1"
          >
            <div className="w-full h-full rounded-full border border-black" />
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
};

export default CameraCaptureCard;
